import React, { useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";

const TYPES = ["", "factory", "trading", "unknown"];
const VERIFIED = ["", "unverified", "verified", "flagged"];

function Field({ label, value, onChange, ph }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <label className="adm-lbl">{label}</label>
      <input className="adm-input" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={ph} />
    </div>
  );
}

// ---------------------------------------------------------------- D3 console
export function SuppliersView({ role }) {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ q: "", category: "", country: "", type: "", verified: "" });
  const [sel, setSel] = useState([]);
  const [tag, setTag] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
    api.get(`/suppliers?${qs}`).then(({ data }) => setRows(data.suppliers)).catch(() => {});
  }, [f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/suppliers/categories").then(({ data }) => setCats(data.categories)).catch(() => {}); }, []);

  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkTag = async () => { if (!tag || !sel.length) return; await api.post("/suppliers/bulk-tag", { ids: sel, category: tag }); setSel([]); setTag(""); load(); };
  const exportCsv = () => { window.open(`${API}/suppliers/export.csv`, "_blank"); };

  return (
    <div data-testid="suppliers-view">
      <div className="adm-h1">Suppliers <button className="btn btn-line" style={{ float: "right", padding: "7px 14px" }} onClick={exportCsv} data-testid="suppliers-export">Export CSV</button></div>
      <div className="adm-sub">Confidential supplier database ({rows.length}). Search across English/Chinese names and products.</div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 6, margin: "10px 0" }}>
        <input className="adm-input" placeholder="Search name / 中文 / product…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="supplier-search" />
        <select className="adm-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}><option value="">All categories</option>{cats.map((c) => <option key={c}>{c}</option>)}</select>
        <input className="adm-input" placeholder="Country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} />
        <select className="adm-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>{TYPES.map((t) => <option key={t} value={t}>{t || "Any type"}</option>)}</select>
        <select className="adm-input" value={f.verified} onChange={(e) => setF({ ...f, verified: e.target.value })}>{VERIFIED.map((t) => <option key={t} value={t}>{t || "Any status"}</option>)}</select>
      </div>
      {sel.length > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <b>{sel.length} selected</b>
          <select className="adm-input" style={{ maxWidth: 200 }} value={tag} onChange={(e) => setTag(e.target.value)}><option value="">Add category…</option>{cats.map((c) => <option key={c}>{c}</option>)}</select>
          <button className="btn btn-navy" style={{ padding: "6px 12px" }} onClick={bulkTag} data-testid="bulk-tag">Apply tag</button>
        </div>
      )}
      <div className="card2" style={{ padding: 0 }}>
        <table className="adm-table" data-testid="suppliers-table">
          <thead><tr><th></th><th>Company</th><th>中文</th><th>Country</th><th>Type</th><th>Categories</th><th>Met at</th></tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} data-testid={`supplier-row-${s.id}`}>
                <td><input type="checkbox" checked={sel.includes(s.id)} onChange={() => toggle(s.id)} /></td>
                <td><button style={{ background: "none", border: "none", color: "var(--navy)", fontWeight: 700, cursor: "pointer" }} onClick={() => setDetail(s)}>{s.company_name}</button></td>
                <td>{s.company_name_cn}</td><td>{s.country}</td><td>{s.type}</td>
                <td style={{ fontSize: 12 }}>{(s.categories || []).join(", ")}</td><td>{s.met_at}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="7" style={{ padding: 20, color: "var(--grey)" }}>No suppliers yet. Use “Import Cards” to add them.</td></tr>}
          </tbody>
        </table>
      </div>
      {detail && <SupplierDetail supplier={detail} role={role} onClose={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function SupplierDetail({ supplier, role, onClose }) {
  const [s, setS] = useState(supplier);
  const [img, setImg] = useState(null);
  const set = (k, v) => setS({ ...s, [k]: v });
  useEffect(() => {
    if (!s.card_image_path) return;
    api.get(`/suppliers/${s.id}/card`, { responseType: "blob" }).then((r) => setImg(URL.createObjectURL(r.data))).catch(() => {});
  }, [s.id, s.card_image_path]);
  const save = async () => { await api.put(`/suppliers/${s.id}`, s); onClose(); };
  const del = async () => { if (window.confirm("Deactivate this supplier?")) { await api.delete(`/suppliers/${s.id}`); onClose(); } };
  return (
    <div style={overlay} onClick={onClose}>
      <div style={drawer} onClick={(e) => e.stopPropagation()} data-testid="supplier-detail">
        <div style={{ display: "flex", justifyContent: "space-between" }}><h2 style={{ color: "var(--navy)" }}>{s.company_name}</h2><button onClick={onClose} style={{ border: "none", background: "none", fontSize: 22, cursor: "pointer" }}>×</button></div>
        {img && <img src={img} alt="card" style={{ width: "100%", borderRadius: 8, marginBottom: 12, border: "1px solid var(--line)" }} data-testid="supplier-card-img" />}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Field label="Company" value={s.company_name} onChange={(v) => set("company_name", v)} />
          <Field label="公司名 (中文)" value={s.company_name_cn} onChange={(v) => set("company_name_cn", v)} />
          <Field label="Contact" value={s.contact} onChange={(v) => set("contact", v)} />
          <Field label="Designation" value={s.designation} onChange={(v) => set("designation", v)} />
          <Field label="Emails (comma)" value={(s.emails || []).join(", ")} onChange={(v) => set("emails", v.split(",").map((x) => x.trim()).filter(Boolean))} />
          <Field label="Phones (comma)" value={(s.phones || []).join(", ")} onChange={(v) => set("phones", v.split(",").map((x) => x.trim()).filter(Boolean))} />
          <Field label="WeChat" value={s.wechat} onChange={(v) => set("wechat", v)} />
          <Field label="Website" value={s.website} onChange={(v) => set("website", v)} />
          <Field label="City" value={s.city} onChange={(v) => set("city", v)} />
          <Field label="Country" value={s.country} onChange={(v) => set("country", v)} />
          <Field label="Categories (comma)" value={(s.categories || []).join(", ")} onChange={(v) => set("categories", v.split(",").map((x) => x.trim()).filter(Boolean))} />
          <div><label className="adm-lbl">Type</label><select className="adm-input" value={s.type} onChange={(e) => set("type", e.target.value)}>{TYPES.slice(1).map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label className="adm-lbl">Verified</label><select className="adm-input" value={s.verified_status} onChange={(e) => set("verified_status", e.target.value)}>{VERIFIED.slice(1).map((t) => <option key={t}>{t}</option>)}</select></div>
          <Field label="Met at" value={s.met_at} onChange={(v) => set("met_at", v)} />
        </div>
        <label className="adm-lbl">Products (free text)</label>
        <textarea className="adm-input" value={s.products_freetext || ""} onChange={(e) => set("products_freetext", e.target.value)} style={{ minHeight: 50 }} />
        <label className="adm-lbl">Notes</label>
        <textarea className="adm-input" value={s.notes || ""} onChange={(e) => set("notes", e.target.value)} style={{ minHeight: 50 }} />
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button className="btn btn-red" onClick={save} data-testid="supplier-save">Save</button>
          {role === "admin" && <button className="btn btn-line" onClick={del}>Deactivate</button>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- D2 card import
export function ImportCardsView() {
  const [files, setFiles] = useState([]);
  const [idx, setIdx] = useState(0);
  const [batch, setBatch] = useState({ met_at: "", met_date: "", relationship_owner: "" });
  const [fields, setFields] = useState(null);
  const [dupes, setDupes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [preview, setPreview] = useState(null);

  const onPick = (e) => { const fl = Array.from(e.target.files).slice(0, 50); setFiles(fl); setIdx(0); setFields(null); setDone(0); };

  const extract = useCallback(async () => {
    if (!files[idx]) return;
    setBusy(true); setFields(null); setDupes([]);
    setPreview(URL.createObjectURL(files[idx]));
    try {
      const fd = new FormData(); fd.append("file", files[idx]);
      const { data } = await api.post("/suppliers/import/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFields({ ...data.fields, ...batch });
      setDupes(data.duplicates || []);
    } catch { setFields({ ...batch }); }
    setBusy(false);
  }, [files, idx, batch]);
  useEffect(() => { if (files.length) extract(); }, [files, idx, extract]);

  const set = (k, v) => setFields({ ...fields, [k]: v });
  const saveNext = async () => {
    await api.post("/suppliers", { ...fields, source: "card-import" });
    setDone((d) => d + 1);
    if (idx + 1 < files.length) setIdx(idx + 1); else { setFiles([]); setFields(null); alert("Batch complete."); }
  };

  return (
    <div data-testid="import-cards-view">
      <div className="adm-h1">Import Cards</div>
      <div className="adm-sub">Drag in up to 50 business-card photos. AI extracts the fields — you review and confirm each one. Nothing is auto-saved.</div>
      <div className="card2">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
          <div><label className="adm-lbl">Batch — met at</label><input className="adm-input" value={batch.met_at} onChange={(e) => setBatch({ ...batch, met_at: e.target.value })} placeholder="e.g. Canton Fair Phase 1" /></div>
          <div><label className="adm-lbl">Met date</label><input className="adm-input" type="date" value={batch.met_date} onChange={(e) => setBatch({ ...batch, met_date: e.target.value })} /></div>
          <div><label className="adm-lbl">Owner</label><input className="adm-input" value={batch.relationship_owner} onChange={(e) => setBatch({ ...batch, relationship_owner: e.target.value })} /></div>
        </div>
        <input type="file" accept="image/*" multiple onChange={onPick} data-testid="card-file-input" />
        {files.length > 0 && <div style={{ marginTop: 8, fontSize: 13, color: "var(--grey)" }}>Card {idx + 1} of {files.length} · {done} saved</div>}
      </div>

      {files.length > 0 && (
        <div className="card2" style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 16 }}>
          <div>{preview && <img src={preview} alt="card" style={{ width: "100%", borderRadius: 8, border: "1px solid var(--line)" }} />}</div>
          <div>
            {busy && <p>Extracting…</p>}
            {fields && (
              <>
                {dupes.length > 0 && <div style={{ background: "#FFF3CD", border: "1px solid #E8C67A", borderRadius: 8, padding: 10, marginBottom: 8, fontSize: 13 }} data-testid="dupe-warning">⚠ Possible duplicate: {dupes.map((d) => d.company_name).join(", ")}</div>}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <Field label="Company" value={fields.company_name} onChange={(v) => set("company_name", v)} />
                  <Field label="公司名 (中文)" value={fields.company_name_cn} onChange={(v) => set("company_name_cn", v)} />
                  <Field label="Contact" value={fields.contact} onChange={(v) => set("contact", v)} />
                  <Field label="Designation" value={fields.designation} onChange={(v) => set("designation", v)} />
                  <Field label="Emails (comma)" value={Array.isArray(fields.emails) ? fields.emails.join(", ") : fields.emails} onChange={(v) => set("emails", v.split(",").map((x) => x.trim()).filter(Boolean))} />
                  <Field label="Phones (comma)" value={Array.isArray(fields.phones) ? fields.phones.join(", ") : fields.phones} onChange={(v) => set("phones", v.split(",").map((x) => x.trim()).filter(Boolean))} />
                  <Field label="WeChat" value={fields.wechat} onChange={(v) => set("wechat", v)} />
                  <Field label="Website" value={fields.website} onChange={(v) => set("website", v)} />
                  <Field label="City" value={fields.city} onChange={(v) => set("city", v)} />
                  <Field label="Country" value={fields.country} onChange={(v) => set("country", v)} />
                </div>
                <Field label="Products" value={fields.products_freetext} onChange={(v) => set("products_freetext", v)} />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button className="btn btn-red" onClick={saveNext} data-testid="save-next">Save &amp; next →</button>
                  <button className="btn btn-line" onClick={() => (idx + 1 < files.length ? setIdx(idx + 1) : setFiles([]))}>Skip</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, background: "rgba(9,25,55,.55)", zIndex: 300, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 30, overflow: "auto" };
const drawer = { background: "#fff", borderRadius: 14, padding: 24, width: 720, maxWidth: "100%" };
